import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  generateImageDescription,
  type GeneratedImageDescription,
  type ImageDescriptionResponse,
} from '@/api/chatRelay';
import { tryJsonParse } from '@/helpers/jsonUtils';
import { getInitialChatRelayState } from '@/stores/types/chatRelay';

const MIN_DESCRIPTIONS = 3;

const mapToDescription = (response: ImageDescriptionResponse): GeneratedImageDescription[] => {
  if (response?.hasError || !response?.imageDescriptions) return [];

  return Object.entries(response.imageDescriptions).map(([key, value]) => ({
    imageUrl: key,
    features: tryJsonParse(value),
  }));
};

export const useChatRelayStore = defineStore('chatRelay', () => {
  const initial = getInitialChatRelayState();
  const connectionId = ref(initial.connectionId);
  const descriptions = ref(initial.descriptions);

  const areImagesProcessed = computed(
    () =>
      Object.values(descriptions.value).every(Boolean) ||
      Object.values(descriptions.value).filter(Boolean).length >= MIN_DESCRIPTIONS,
  );

  const setImageDescription = ({
    imageUrl,
    valueToSet,
  }: {
    imageUrl: string;
    valueToSet: GeneratedImageDescription | undefined;
  }): void => {
    descriptions.value = { ...descriptions.value, [imageUrl]: valueToSet };
  };

  const removeImageDescription = (url: string): void => {
    const nextDescriptions = { ...descriptions.value };
    delete nextDescriptions[url];
    descriptions.value = nextDescriptions;
  };

  const clearImageDescriptions = (): void => {
    descriptions.value = {};
  };

  const processImage = async (imageUrl: string): Promise<GeneratedImageDescription | undefined> => {
    setImageDescription({ imageUrl, valueToSet: undefined });
    const response = await generateImageDescription(connectionId.value!, [imageUrl]);
    const generatedImageDescription = mapToDescription(response).shift() || {
      imageUrl,
    };
    setImageDescription({ imageUrl, valueToSet: generatedImageDescription });
    return generatedImageDescription;
  };

  return {
    connectionId,
    descriptions,
    areImagesProcessed,
    removeImageDescription,
    clearImageDescriptions,
    processImage,
  };
});
